import { useMemo } from 'react';
import {
  Printer, Globe, ShoppingBag, LayoutGrid, Image as ImageIcon,
  CreditCard, Mail, Search, ShieldCheck, Server, Wrench, Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSeo } from '@/hooks/useSeo';

const euro = (n) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(n);

const devItems = [
  { icon: LayoutGrid, label: 'Progettazione UI/UX & identità digitale del brand', desc: 'Design editoriale su misura, palette e tipografia Playfair, animazioni e cursore custom.', price: 600 },
  { icon: ShoppingBag, label: 'Sviluppo front-end (React)', desc: 'Home, collezione, schede prodotto, carrello, checkout, preferiti, pagine istituzionali.', price: 900 },
  { icon: Server, label: 'Sviluppo back-end (FastAPI) & database (MongoDB)', desc: 'API sicure, autenticazione, streaming immagini/video, logica ordini e carrello.', price: 700 },
  { icon: ShieldCheck, label: 'Pannello di amministrazione completo', desc: 'Gestione prodotti, ordini con PDF, appuntamenti e codici sconto per categoria.', price: 650 },
  { icon: ImageIcon, label: 'CMS Homepage dinamico', desc: 'Testi, immagini e video della home modificabili in autonomia dal pannello.', price: 350 },
  { icon: CreditCard, label: 'Integrazione pagamenti Stripe + Klarna', desc: 'Carta, Apple Pay, Link e pagamento a rate Klarna in euro.', price: 300 },
  { icon: Mail, label: 'Email automatiche & PDF ordini', desc: 'Conferma d\u2019ordine transazionale brandizzata e documento PDF generato in automatico.', price: 200 },
  { icon: Search, label: 'SEO dinamica, ottimizzazione & mobile', desc: 'Meta tag dinamici, performance e piena resa responsive su smartphone.', price: 200 },
];

const yearlyItems = [
  { icon: Globe, label: 'Registrazione e gestione dominio cirogallotti.com', price: 30 },
  { icon: Server, label: 'Hosting & infrastruttura cloud', price: 180 },
  { icon: Wrench, label: 'Manutenzione, aggiornamenti di sicurezza & assistenza', price: 190 },
];

const highlights = [
  'E-commerce completo con carrello e checkout',
  'Pagamenti sicuri Stripe + rate Klarna',
  'Pannello admin per gestire tutto in autonomia',
  'CMS Home: testi, immagini e video modificabili',
  'Ordini con PDF ed email automatiche',
  'Prenotazione appuntamenti in showroom',
  'Codici sconto e gestione disponibilità',
  'SEO ottimizzata e design 100% responsive',
];

export default function PreventivoPage() {
  useSeo({
    title: 'Preventivo Digitale — Ciro Gallotti Jewelry',
    description: 'Preventivo digitale riservato per il sito e-commerce Ciro Gallotti.',
    path: '/preventivo',
  });

  const { dateStr, validStr, devTotal, yearlyTotal, quoteNo } = useMemo(() => {
    const now = new Date();
    const valid = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const fmt = (d) => d.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
    return {
      dateStr: fmt(now),
      validStr: fmt(valid),
      devTotal: devItems.reduce((s, i) => s + i.price, 0),
      yearlyTotal: yearlyItems.reduce((s, i) => s + i.price, 0),
      quoteNo: `CG-${now.getFullYear()}-001`,
    };
  }, []);

  return (
    <div className="bg-ivory pt-24 md:pt-28 preventivo-root" data-testid="preventivo-page">
      {/* Floating actions (not printed) */}
      <div className="no-print sticky top-24 z-30 px-6 md:px-12 lg:px-16">
        <div className="max-w-container mx-auto flex justify-end">
          <Button
            onClick={() => window.print()}
            variant="accent"
            size="xl"
            data-testid="preventivo-print-btn"
            className="shadow-lg"
          >
            <Printer className="w-4 h-4" />
            Stampa / Scarica PDF
          </Button>
        </div>
      </div>

      <div className="max-w-container mx-auto px-6 md:px-12 lg:px-16 pb-24 pt-6 preventivo-sheet">
        {/* Header */}
        <header className="hairline-b pb-10 mb-12">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-12 h-px bg-bordeaux" />
            <p className="text-eyebrow text-bordeaux">Documento riservato</p>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div>
              <h1 className="font-display text-h1 text-ink leading-none">
                Preventivo <span className="italic">Digitale</span>
              </h1>
              <p className="mt-5 max-w-xl text-ink/65 leading-relaxed">
                Progettazione e sviluppo del sito e-commerce <strong className="text-ink">Ciro Gallotti — Jewelry Design</strong>,
                comprensivo di pannello di gestione, pagamenti online e dominio dedicato.
              </p>
            </div>
            <div className="font-display text-right shrink-0">
              <div className="text-4xl tracking-tight text-ink">CG</div>
              <div className="text-eyebrow text-ink/50 mt-1">Jewelry Design</div>
            </div>
          </div>
        </header>

        {/* Meta grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-line border border-line mb-16">
          {[
            { k: 'Emesso da', v: 'Renato Maione' },
            { k: 'Intestato a', v: 'Ciro Gallotti' },
            { k: 'Data', v: dateStr },
            { k: 'N. preventivo', v: quoteNo },
          ].map((m) => (
            <div key={m.k} className="bg-ivory p-5">
              <p className="text-eyebrow text-ink/50 mb-2">{m.k}</p>
              <p className="text-sm text-ink font-medium capitalize">{m.v}</p>
            </div>
          ))}
        </div>

        {/* Project summary */}
        <section className="mb-16">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-8 h-px bg-bordeaux" />
            <h2 className="text-eyebrow text-bordeaux">Il Progetto</h2>
          </div>
          <p className="text-ink/70 leading-relaxed max-w-3xl mb-10">
            Una piattaforma e-commerce di lusso realizzata interamente su misura — dalla grafica al codice —
            pensata per vendere online i gioielli, raccontare il brand e gestire l’intera attività in totale autonomia,
            senza costi per piattaforme di terze parti.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
            {highlights.map((h) => (
              <div key={h} className="flex items-start gap-3">
                <Check className="w-4 h-4 text-bordeaux mt-1 shrink-0" strokeWidth={2} />
                <span className="text-sm text-ink/75">{h}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Dev line items */}
        <section className="mb-16">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-8 h-px bg-bordeaux" />
            <h2 className="text-eyebrow text-bordeaux">Dettaglio lavorazioni — una tantum</h2>
          </div>
          <div className="border border-line">
            {devItems.map((it, idx) => (
              <div
                key={it.label}
                className={`flex items-start gap-5 p-5 md:p-6 ${idx !== 0 ? 'hairline-t' : ''}`}
                data-testid="preventivo-dev-row"
              >
                <div className="w-10 h-10 border border-line rounded-full flex items-center justify-center shrink-0">
                  <it.icon className="w-4 h-4 text-bordeaux" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-ink font-medium">{it.label}</h3>
                  <p className="text-sm text-ink/55 mt-1 leading-relaxed">{it.desc}</p>
                </div>
                <div className="font-display text-lg text-ink whitespace-nowrap tabular-nums">{euro(it.price)}</div>
              </div>
            ))}
            <div className="hairline-t flex items-center justify-between p-5 md:p-6 bg-ink text-ivory">
              <span className="text-eyebrow">Totale sviluppo (una tantum)</span>
              <span className="font-display text-2xl tabular-nums" data-testid="preventivo-dev-total">{euro(devTotal)}</span>
            </div>
          </div>
        </section>

        {/* Yearly */}
        <section className="mb-16">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-8 h-px bg-bordeaux" />
            <h2 className="text-eyebrow text-bordeaux">Canone annuale</h2>
          </div>
          <div className="border border-line">
            {yearlyItems.map((it, idx) => (
              <div
                key={it.label}
                className={`flex items-center gap-5 p-5 md:p-6 ${idx !== 0 ? 'hairline-t' : ''}`}
              >
                <div className="w-10 h-10 border border-line rounded-full flex items-center justify-center shrink-0">
                  <it.icon className="w-4 h-4 text-bordeaux" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-ink font-medium">{it.label}</h3>
                </div>
                <div className="font-display text-lg text-ink whitespace-nowrap tabular-nums">{euro(it.price)}<span className="text-xs text-ink/50">/anno</span></div>
              </div>
            ))}
            <div className="hairline-t flex items-center justify-between p-5 md:p-6 bg-ink/5">
              <span className="text-eyebrow text-ink">Totale canone</span>
              <span className="font-display text-xl text-ink tabular-nums" data-testid="preventivo-yearly-total">{euro(yearlyTotal)}<span className="text-xs text-ink/50">/anno</span></span>
            </div>
          </div>
        </section>

        {/* Grand summary */}
        <section className="mb-14">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line">
            <div className="bg-ivory p-8 flex flex-col justify-between">
              <p className="text-eyebrow text-ink/50 mb-4">Realizzazione sito</p>
              <div>
                <span className="font-display text-5xl text-ink tabular-nums">{euro(devTotal)}</span>
                <p className="text-sm text-ink/55 mt-2">Una tantum · pagamento alla consegna</p>
              </div>
            </div>
            <div className="bg-bordeaux text-ivory p-8 flex flex-col justify-between">
              <p className="text-eyebrow text-ivory/70 mb-4">Gestione & mantenimento</p>
              <div>
                <span className="font-display text-5xl tabular-nums">{euro(yearlyTotal)}</span>
                <p className="text-sm text-ivory/70 mt-2">Canone annuale · dominio, hosting, assistenza</p>
              </div>
            </div>
          </div>
        </section>

        {/* Notes */}
        <section className="hairline-t pt-10">
          <p className="text-eyebrow text-ink/50 mb-4">Note</p>
          <ul className="space-y-2 text-sm text-ink/65 leading-relaxed max-w-3xl">
            <li>• Il primo anno di dominio è incluso nel canone annuale indicato.</li>
            <li>• Importi non comprensivi di eventuale IVA, se applicabile.</li>
            <li>• Preventivo valido fino al <strong className="text-ink">{validStr}</strong>.</li>
            <li>• Il canone annuale decorre dalla pubblicazione online del sito.</li>
          </ul>

          <div className="mt-12 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8">
            <div>
              <p className="text-eyebrow text-ink/50 mb-2">Preventivo a cura di</p>
              <p className="font-display text-2xl text-ink">Renato Maione</p>
            </div>
            <div className="text-sm text-ink/55">
              <p>Grazie per la fiducia.</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
